import * as React from 'react';

import { throttle } from 'throttle-debounce';

import { isMobileScreen } from '@base/utils';

interface ITouchParams {
	start?: ITouchParam;
	end?: ITouchParam;
}

interface ITouchParam {
	x: number;
	y: number;
	timeStamp: number;
}

interface IUseTouchMenu {
	mainMenuViewRef: React.RefObject<HTMLDivElement>;
	hideMenu(): void;
}

const SWIPE_TO_CLOSE_LENGTH = 50;
const SWIPE_TO_CLOSE_VERTICAL_LIMIT = 200;
const SWIPE_TO_CLOSE_TIME_LIMIT = 600;

export function useTouchMenu({ mainMenuViewRef, hideMenu }: IUseTouchMenu) {
	let touchParams: ITouchParams = {
		start: null,
		end: null,
	};
	const onTouchStart = (event: TouchEvent) => {
		touchParams.start = {
			x: event.changedTouches[0].clientX,
			y: event.changedTouches[0].clientY,
			timeStamp: event.timeStamp,
		};
	};
	const onTouchEnd = (event: TouchEvent) => {
		touchParams.end = {
			x: event.changedTouches[0].clientX,
			y: event.changedTouches[0].clientY,
			timeStamp: event.timeStamp,
		};
		const isHorizontalSwipeEnough = touchParams.start.x - touchParams.end.x > SWIPE_TO_CLOSE_LENGTH;
		const isVerticalSwipeEnough = Math.abs(touchParams.start.y - touchParams.end.y) < SWIPE_TO_CLOSE_VERTICAL_LIMIT;
		const isManagedSwipeInTime = touchParams.end.timeStamp - touchParams.start.timeStamp < SWIPE_TO_CLOSE_TIME_LIMIT;

		if (isHorizontalSwipeEnough && isVerticalSwipeEnough && isManagedSwipeInTime) {
			hideMenu();
		}

		touchParams = {
			start: null,
			end: null,
		};
	};

	function addTouchListener() {
		const menu = mainMenuViewRef?.current;

		menu?.addEventListener('touchstart', onTouchStart, { passive: true });
		menu?.addEventListener('touchend', onTouchEnd, false);
	}
	function removeTouchListener() {
		const menu = mainMenuViewRef?.current;

		menu?.removeEventListener('touchstart', onTouchStart, false);
		menu?.removeEventListener('touchend', onTouchEnd, false);
	}

	const onResize = throttle(1000, () => {
		if (isMobileScreen()) {
			addTouchListener();
		} else {
			hideMenu();
			removeTouchListener();
		}
	});

	React.useEffect(() => {
		if (!mainMenuViewRef && !mainMenuViewRef.current) {
			return;
		}

		window.addEventListener('resize', onResize);
		if (isMobileScreen()) {
			addTouchListener();
		}

		return () => {
			window.removeEventListener('resize', onResize);
			removeTouchListener();
		};
	}, []);
}
